/**
 * Toast — minimale, abhängigkeitsfreie Benachrichtigung (UX_KONZEPT.md §4
 * Punkt 3/6, §6 Annahme 4: keine neue UI-Bibliothek, ~30 Zeilen Eigenlösung).
 *
 * Verwendung:
 *   const toast = useToast()
 *   toast.show("Gespeichert.", "success")
 *   toast.show("Fehler beim Laden.", "error")
 */
import { createContext, useCallback, useContext, useState } from "react"

type ToastKind = "success" | "error" | "info"

interface ToastEntry {
  id: number
  message: string
  kind: ToastKind
}

interface ToastContextValue {
  show: (message: string, kind?: ToastKind) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/** Icon-Punkt-Farbe je Toast-Art (UX_KONZEPT.md §6.6: Erfolg grün, Fehler rot). */
const DOT_STYLES: Record<ToastKind, string> = {
  success: "bg-green-500",
  error: "bg-red-500",
  info: "bg-slate-400",
}

let nextId = 1

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const show = useCallback((message: string, kind: ToastKind = "info") => {
    const id = nextId++
    setToasts((t) => [...t, { id, message, kind }])
    window.setTimeout(() => {
      setToasts((t) => t.filter((entry) => entry.id !== id))
    }, 4000)
  }, [])

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
        aria-live="polite"
        role="status"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-lg"
          >
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_STYLES[t.kind]}`} aria-hidden="true" />
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error("useToast() muss innerhalb von <ToastProvider> verwendet werden")
  }
  return ctx
}
